import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnterpriseRequest } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { AVISO_HREF, AVISO_PUBLICADO } from "@/lib/aviso";

const FIELDS = [
  { key: "name", type: "text", required: true, testid: "modal-input-name" },
  { key: "email", type: "email", required: true, testid: "modal-input-email" },
  { key: "company", type: "text", required: true, testid: "modal-input-company" },
  { key: "capacity_mw", type: "text", required: false, testid: "modal-input-capacity" },
];

export default function EnterpriseModal({ open, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", capacity_mw: "", message: "" });
  // Casillas del aviso de privacidad: la primera obligatoria, la segunda opcional y sin marcar.
  const [privacidad, setPrivacidad] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      toast.error(t.modal.toasts.invalid);
      return;
    }
    if (!privacidad) {
      toast.error(t.modal.toasts.consent);
      return;
    }
    setLoading(true);
    try {
      await submitEnterpriseRequest({ ...form, consentimiento_privacidad: true, consentimiento_marketing: marketing });
      setSent(true);
      toast.success(t.modal.toasts.success);
    } catch (err) {
      toast.error(t.modal.toasts.error);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setPrivacidad(false);
      setMarketing(false);
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <div data-testid="enterprise-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#0B1630]/85 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-white rounded-2xl p-8 max-h-[92vh] overflow-y-auto"
          >
            <button
              data-testid="modal-close-btn"
              onClick={close}
              className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#64748B] hover:border-[#C5B358] hover:text-[#C5B358] transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {sent ? (
              <div className="py-10 text-center" data-testid="modal-success-state">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#C5B358]/15 flex items-center justify-center">
                  <Check size={26} strokeWidth={2.5} className="text-[#C5B358]" />
                </div>
                <h3 className="font-display mt-6 text-2xl font-bold text-[#0F1E3D]">{t.modal.successTitle}</h3>
                <p className="mt-3 text-sm text-[#64748B] leading-relaxed max-w-xs mx-auto">
                  {t.modal.successBody}
                </p>
                <button
                  data-testid="modal-success-done-btn"
                  onClick={close}
                  className="mt-8 bg-[#0F1E3D] text-white text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-8 py-3.5 hover:bg-[#16284F] transition-colors"
                >
                  {t.modal.done}
                </button>
              </div>
            ) : (
              <>
                <div className="eyebrow text-[#C5B358]">{t.modal.overline}</div>
                <h3 className="font-display mt-3 text-2xl font-bold text-[#0F1E3D]">
                  {t.modal.titleA}
                  <em className="font-serif-accent text-[#C5B358]">{t.modal.titleEm}</em>
                  {t.modal.titleB}
                </h3>
                <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{t.modal.desc}</p>

                <form onSubmit={submit} className="mt-8 space-y-6" noValidate>
                  {FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className="eyebrow text-[#94A3B8] flex items-center gap-1">
                        {t.modal.fields[f.key]}
                        {f.required && <span className="text-[#C5B358]">*</span>}
                      </label>
                      <input
                        type={f.type}
                        data-testid={f.testid}
                        value={form[f.key]}
                        onChange={set(f.key)}
                        required={f.required}
                        className="mt-2 w-full border-b border-[#E2E8F0] focus:border-[#C5B358] outline-none py-2.5 bg-transparent text-[#1E293B] transition-colors"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="eyebrow text-[#94A3B8]">{t.modal.fields.message}</label>
                    <textarea
                      data-testid="modal-input-message"
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      className="mt-2 w-full border-b border-[#E2E8F0] focus:border-[#C5B358] outline-none py-2.5 bg-transparent text-[#1E293B] resize-none transition-colors"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 text-[13px] text-[#1E293B] leading-snug cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="modal-consent-privacy"
                        checked={privacidad}
                        onChange={(e) => setPrivacidad(e.target.checked)}
                        required
                        className="mt-0.5 w-4 h-4 accent-[#0F1E3D] flex-shrink-0"
                      />
                      <span>
                        {t.modal.consent.privacyA}
                        {AVISO_PUBLICADO ? (
                          <a href={AVISO_HREF} target="_blank" rel="noopener noreferrer" className="underline text-[#0F1E3D]">
                            {t.modal.consent.privacyLink}
                          </a>
                        ) : (
                          t.modal.consent.privacyLink
                        )}
                        {t.modal.consent.privacyB}
                        <span className="text-[#C5B358]"> *</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 text-[13px] text-[#64748B] leading-snug cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="modal-consent-marketing"
                        checked={marketing}
                        onChange={(e) => setMarketing(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#0F1E3D] flex-shrink-0"
                      />
                      <span>{t.modal.consent.marketing}</span>
                    </label>
                  </div>
                  <button
                    type="submit"
                    data-testid="modal-submit-btn"
                    disabled={loading}
                    className="w-full bg-[#C5B358] hover:bg-[#B3A146] text-[#0F1E3D] rounded-lg py-4 text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading && <Loader2 size={14} className="animate-spin" />}
                    {t.modal.submit}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}